import { BaseLegacy } from './base';
import { HtmlText } from './htmlText';
import { MbfObject } from './mediabank';
import { PermissionLegacy } from './permission';
import { Todo } from './todo';
import { Unit } from './unit';
import { UserBaseLegacy, UserRelationLegacy, UserRelationType } from './user';

export type EventListFilter = 'all' | 'open' | 'rsvp' | 'missing-coorganizer' | string;

export const EventActiveModuleKeys = {
	Intro: 'intro',
	CheckIn: 'checkin',
	Quiz: 'quiz',
	Polls: 'polls',
	Comments: 'comments',
	Whiteboard: 'whiteboard',
	Files: 'files',
	Chat: 'chat',
} as const;

export type EventActiveModuleKey = (typeof EventActiveModuleKeys)[keyof typeof EventActiveModuleKeys];

export type Event = {
	Id: number;
	EventTypeId: number;
	CreatedByUserId: number;
	PortalId: number;
	CreatedDateUtc: Date;
	StartDateTimeUtc: Date;
	EndDateTimeUtc: Date;
	RSVPDateTimeUtc: Date;
	ExternalUrl: string;
	MaxParticipants: number;
	IsActive: boolean;
	Organizer: UserBaseLegacy | null;
	OrganizerName: string;
	OrganizerEmail: string;
	OrganizerPhone: string;
	OrganizerPhotoUrl: string | null;
	Status: EventStatus;
	IsExternal: boolean;
	CultureIds: number[];
	Name: string;
	Description: string;
	ShortDescription: string;
	InternalDescription: string | null;
	Location: string;
	ImageUrl: string | null;
	InternalComment: string | null;
	DoNotRequireRSVP: boolean;
	AskForFoodPreferences: boolean;
	ParticipantCount: number;
	UserStatus: ParticipationStatus;
	UserComment: string | null;
	Todos: Todo[] | null;
	CoOrganizers: EventCoOrganizer[];
	CanParticipate: boolean;
	RelatedParticipationUserIds: number[] | null;
	HtmlTexts: HtmlText[] | null;
	Permissions: PermissionLegacy[] | null;
	MbfObject: MbfObject | null;
	ActiveModules: EventActiveModuleKey[] | null;
	IsWaitlistEnabled: boolean;
	MissingOrganizerCount: number;
	SubstituteRecruitmentReleased?: boolean;
	IsAllowedToHandleEvent: boolean;
	BlockParticipationStatusChange: boolean | null;
};

export interface EventCoOrganizer extends UserBaseLegacy {
	IsAbsent: boolean;
	IsSubstitute: boolean;
}

export type GeneralEventFrontEndClientSettings = {
	IsEventStaffingEnabled?: boolean;
	ManualEventSubstituteRelease?: boolean;
	AllowSelfAddSubstituteCoOrganizers: boolean;
	AllowCoOrganizersToRegisterAbsence: boolean;
	ShowChatOnOneEventWithoutObjectLink: boolean;
};

export enum EventStatus {
	Upcoming = 0,
	Cancelled = 1,
	Completed = 2,
}

export enum ParticipationStatus {
	None = 0,
	Invited = 2,
	Attending = 3,
	NotAttending = 4,
	ConfirmedAttended = 5,
	Pending = 6,
	Waitlisted = 7,
}

export enum UpdateParticipationStatusResult {
	CannotFindEvent = 1,
	EventIsFull = 2,
	EventIsExternal = 3,
	RSVPDatePassed = 4,
	InvalidEmail = 5,
	StatusUpdateMailSent = 6,
	FailedToSendUpdateMail = 7,
	UserWaitlisted = 8,
}

export type EventParticipator = {
	Id: number;
	EventId: number;
	ParticipantUserId: number;
	Status: ParticipationStatus;
	ParticipantName: string;
	MainUserId: number | null;
	MainUserName: string | null;
	MainUserRelationType: UserRelationType | null;
	UserComment: string | null;
	Comment: string | null;
	FoodPreference: string | null;
	AllUserRelations: UserRelationLegacy[] | null;
};

export type EventStatsEvent = {
	EventId: number;
	EventName: string;
	EventStart: Date;
	EventEnd: Date;
	RsvpStatus: string;
	AttendedCount: number;
	AttendedByUserRelationGroupsCount: number;
	AttendedByUserRelation: EventStatsByUserRelationCount[];
	AttendedByUserIds: number[];
	Permissions: PermissionLegacy[];
	EventCategoryIds: number[];
};

export type EventStatsByUserRelationCount = {
	UserRelationSystemName: string;
	Count: number;
	UserIds: number[];
};

export type EventStats = {
	EventStats: EventStatsEvent[];
	TotalAttendedCount: number;
	AllAttendedUserIds: number[];
	TotalAttendedByUserRelationGroupCount: number;
	TotalUniqueAttendedByUserRelationGroupCount: number;
	UsersWAccess: EventUserStatsEntity[];
	DummyUsersWAccess: EventUserStatsEntity[];
	TotalAttendedByUserRelation: EventStatsByUserRelationCount[];
	TotalEventTimeInMinutes: number;
	TotalAttendedEventTimeInMinutes: number;
};

export interface EventUserStatsEntity extends UserBaseLegacy {
	HasAttended: boolean;
	Units: Unit[] | null;
	AccessToEvents: number[] | null;
	AttendedEvents: number[] | null;
	AttendedEventsByRelationGroup: number[] | null;
}

export interface EventCategory extends BaseLegacy {
	Order: number;
}
